import type { Block } from "../../../shared/schema";
import type { TemplateContext } from "../../../shared/template/types";
import { interpolateText, hasTemplateVariables } from "../../../shared/template/interpolate";

/**
 * Text fields that support template interpolation, by block type.
 */
const INTERPOLATABLE_FIELDS: Record<string, string[]> = {
  text: ["content"],
  heading: ["text"],
  button: ["text"],
  card: ["title", "description"],
  banner: ["content"],
  html: ["content"],
  "rich-text": ["content"],
  iframe: ["url", "title"],
};

/**
 * Interpolate template variables in a block's text props.
 * Returns a new block object with interpolated values.
 * Returns the original block reference if no interpolation is needed (optimization).
 */
export function interpolateBlockProps<T extends Block>(block: T, context: TemplateContext): T {
  const fields = INTERPOLATABLE_FIELDS[block.type];
  if (!fields) return block;

  // Check if any field has template variables (optimization)
  const props = block.props as Record<string, unknown>;
  const needsInterpolation = fields.some((field) => hasTemplateVariables(props[field] as string));

  if (!needsInterpolation) return block;

  // Create new props with interpolated values
  const newProps = { ...props };
  for (const field of fields) {
    const value = props[field];
    if (typeof value === "string" && hasTemplateVariables(value)) {
      newProps[field] = interpolateText(value, context);
    }
  }

  return { ...block, props: newProps } as T;
}
